import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    super.connectedCallback();

    if (!('IntersectionObserver' in window)) {
      this.#loadSources();
      return;
    }

    // Only fetch the video once the section approaches the viewport, and pause it while off-screen,
    // instead of downloading every background video on the page at load.
    this.#observer = new IntersectionObserver(
      (entries) => {
        const { videoElement } = this.refs;
        if (entries[0]?.isIntersecting) {
          if (!this.hasAttribute('data-loaded')) this.#loadSources();
          else videoElement.play()?.catch(() => {});
        } else if (videoElement && !videoElement.paused) {
          videoElement.pause();
        }
      },
      { rootMargin: '300px 0px' }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
  }

  #loadSources() {
    const { videoSources, videoElement } = this.refs;

    this.setAttribute('data-loaded', '');

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.load();
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
